import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  RANGE_CONFIG,
  buildLogChartPath,
  fetchDeviceLogs,
  formatPowerValue,
  formatTimestamp,
  getLogChartPoint,
  getLogRangeView,
  getPowerExport,
  getPowerImport,
} from '../services/deviceLogs'
import './DeviceLogsContent.css'

const SERIES = [
  { key: 'powerImport', label: 'Power Import', color: '#00c853' },
  { key: 'powerExport', label: 'Power Export', color: '#f59e0b' },
]

function DeviceLogsContent({ deviceId }) {
  const [logs, setLogs] = useState([])
  const [status, setStatus] = useState('idle')
  const [activeRange, setActiveRange] = useState('day')
  const [tooltip, setTooltip] = useState(null)
  const chartRef = useRef(null)

  useEffect(() => {
    if (!deviceId) {
      setLogs([])
      setStatus('empty')
      return undefined
    }

    let isActive = true
    setLogs([])
    setTooltip(null)
    setActiveRange('day')
    setStatus('loading')

    fetchDeviceLogs(deviceId)
      .then((fetchedLogs) => {
        if (!isActive) {
          return
        }

        setLogs(fetchedLogs)
        setStatus(fetchedLogs.length ? 'ready' : 'empty')
      })
      .catch((error) => {
        console.error(`Failed to load Firebase logs for ${deviceId}:`, error)

        if (isActive) {
          setLogs([])
          setStatus('error')
        }
      })

    return () => {
      isActive = false
    }
  }, [deviceId])

  const rangeView = useMemo(() => getLogRangeView(logs, activeRange), [activeRange, logs])

  const showMarkerTooltip = (event, marker) => {
    if (!chartRef.current) {
      return
    }

    event.preventDefault()

    if (event.type === 'pointerdown' && event.currentTarget?.setPointerCapture && event.pointerId !== undefined) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId)
      } catch (error) {
        // Some browsers only allow capture while an active pointer is down.
      }
    }

    const chartRect = chartRef.current.getBoundingClientRect()

    setTooltip({
      x: event.clientX - chartRect.left + 14,
      y: event.clientY - chartRect.top + 14,
      label: formatTimestamp(marker.log.timestamp),
      series: marker.series.label,
      value: formatPowerValue(marker.point.value),
    })
  }

  const renderContent = () => {
    if (status === 'loading') {
      return <div className="device-logs-content__status">Loading logs...</div>
    }

    if (status === 'error') {
      return <div className="device-logs-content__status">Failed to load logs for {deviceId}.</div>
    }

    if (status === 'empty' || !rangeView) {
      return <div className="device-logs-content__status">No timestamped logs found for {deviceId || 'this device'}.</div>
    }

    const { dimensions, gridLines, scale, chartLogs, descendingLogs } = rangeView
    const chartWidth = dimensions.width - dimensions.right
    const markerData = SERIES.flatMap((series) =>
      chartLogs.map((log, index) => ({
        series,
        log,
        point: getLogChartPoint(log, index, chartLogs, series.key, scale, dimensions),
      }))
    )

    return (
      <>
        <div className="device-logs-content__toolbar">
          <div className="device-logs-content__ranges" role="group" aria-label="Log range">
            {Object.entries(RANGE_CONFIG).map(([range, config]) => (
              <button
                className={`device-logs-content__range ${activeRange === range ? 'device-logs-content__range--active' : ''}`}
                key={range}
                type="button"
                onClick={() => {
                  setActiveRange(range)
                  setTooltip(null)
                }}
              >
                {config.label}
              </button>
            ))}
          </div>
          <div className="device-logs-content__meta">{rangeView.metaText}</div>
        </div>

        <div className="device-logs-content__chart" ref={chartRef}>
          <div className="device-logs-content__chart-header">
            <h3>Power Import vs Power Export</h3>
            <div className="device-logs-content__legend">
              {SERIES.map((series) => (
                <span key={series.key}>
                  <i style={{ background: series.color }} aria-hidden="true"></i>
                  {series.label}
                </span>
              ))}
            </div>
          </div>

          {tooltip && (
            <div className="device-logs-content__tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
              <div>{tooltip.label}</div>
              <strong>
                {tooltip.series}: {tooltip.value}
              </strong>
            </div>
          )}

          <svg viewBox={`0 0 ${dimensions.width} ${dimensions.height}`} role="img" aria-label="Power import and export chart">
            <rect x="0" y="0" width={dimensions.width} height={dimensions.height} fill="transparent"></rect>
            {gridLines.map((line) => (
              <React.Fragment key={line.y}>
                <line x1={dimensions.left} y1={line.y} x2={chartWidth} y2={line.y} stroke="var(--logs-content-grid)" />
                <text x={dimensions.left - 12} y={line.y + 4} textAnchor="end" fontSize="12" fill="var(--logs-content-muted)">
                  {line.value.toFixed(2)}
                </text>
              </React.Fragment>
            ))}
            {SERIES.map((series) => (
              <path
                key={series.key}
                d={buildLogChartPath(chartLogs, series.key, scale, dimensions)}
                fill="none"
                stroke={series.color}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
            ))}
            {markerData.map((marker) => (
              <React.Fragment key={`${marker.series.key}-${marker.log.timestamp}`}>
                <circle
                  className="device-logs-content__marker"
                  cx={marker.point.x.toFixed(2)}
                  cy={marker.point.y.toFixed(2)}
                  r="4"
                  fill={marker.series.color}
                  stroke="var(--logs-content-navy-deep)"
                  strokeWidth="1.8"
                ></circle>
                <circle
                  className="device-logs-content__marker-hit"
                  cx={marker.point.x.toFixed(2)}
                  cy={marker.point.y.toFixed(2)}
                  r="16"
                  onPointerEnter={(event) => showMarkerTooltip(event, marker)}
                  onPointerDown={(event) => showMarkerTooltip(event, marker)}
                  onPointerMove={(event) => showMarkerTooltip(event, marker)}
                  onPointerLeave={() => setTooltip(null)}
                  onPointerCancel={() => setTooltip(null)}
                ></circle>
              </React.Fragment>
            ))}
            <text x={dimensions.left} y={dimensions.height - 10} fontSize="12" fill="var(--logs-content-muted)">
              {rangeView.startLabel}
            </text>
            <text x={chartWidth} y={dimensions.height - 10} textAnchor="end" fontSize="12" fill="var(--logs-content-muted)">
              {rangeView.endLabel}
            </text>
          </svg>
        </div>

        <div className="device-logs-content__table">
          <table>
            <thead>
              <tr>
                <th scope="col">Timestamp</th>
                <th scope="col">Power Import</th>
                <th scope="col">Power Export</th>
              </tr>
            </thead>
            <tbody>
              {descendingLogs.map((log) => (
                <tr key={log._key || log.timestamp}>
                  <td>{formatTimestamp(log.timestamp)}</td>
                  <td>{formatPowerValue(getPowerImport(log))}</td>
                  <td>{formatPowerValue(getPowerExport(log))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    )
  }

  return <div className="device-logs-content">{renderContent()}</div>
}

export default DeviceLogsContent
